import { useState } from "react";
import { Button } from "./ui/button";
import { Check, ChevronDown, ChevronRight, Loader2, Pencil, X } from "lucide-react";
import { useTaal } from "@/contexts/TaalContext";
import { herkomstTekst } from "@/lib/geheugenregel";

/**
 * The tray: what Novamund thinks it noticed, waiting to be looked at.
 *
 * **THIS BLOCK IS THE WHOLE REASON PHASE A IS ALLOWED TO STORE ANYTHING.** The
 * build plan of 29 July, section 1.2, names the tension out loud: a candidate
 * is kept in a table, and keeping it is only honest because it is VISIBLE, it
 * weighs nowhere, and one click removes it. This component is the "visible".
 * Take it away and what is left is quiet storage, which is the thing the whole
 * design exists to prevent.
 *
 * **QUIET ON PURPOSE, AND THAT IS REQUIREMENT 5.** Never a pop-up, never a
 * dialog, never a red dot demanding attention. A block that shouts turns into
 * something to dismiss rather than something to read, and this one is asking
 * permission. When there is nothing waiting it renders nothing at all: an
 * empty box that says "no suggestions" is a box that trains you to skip it.
 *
 * **THE QUOTES ARE THE POINT OF THE CARD, and they are folded away.** Section
 * 1.4 requirement 1: at least two independent quotes, or the candidate never
 * got here. Showing them all open would turn eight rows into a wall of
 * conversation; hiding them entirely would make "why" a claim rather than
 * something you can check. So they are one click away, per row, and the button
 * carries `aria-expanded` so a screen reader knows which it is.
 *
 * **WOORDEN OP HET SCHERM VOLGEN DE TAAL VAN DE APP** (Herstel Geheugen NL,
 * 21 oktober 2026). Hier stond dat het blok Engels was om bij het scherm te
 * passen; sinds de taalschakelaar is het scherm zelf NL of EN, en "10 waiting",
 * "Accept all 10" en "Why (2)" bleven als enige Engels staan.
 */

function Citaten({ quotes, open, onToggle, id }) {
  const { t } = useTaal();
  if (!quotes || quotes.length === 0) return null;
  const Pijl = open ? ChevronDown : ChevronRight;
  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`quotes-${id}`}
        data-testid={`suggested-why-${id}`}
        className="mt-1 -ml-1 flex items-center gap-1 rounded px-1 py-0.5 text-xs text-muted-foreground hover:text-foreground"
      >
        <Pijl className="h-3 w-3 shrink-0" />
        {open ? t("Verberg waarom") : t("Waarom ({aantal})", { aantal: quotes.length })}
      </button>
      {open && (
        <ul
          id={`quotes-${id}`}
          data-testid={`suggested-quotes-${id}`}
          className="mt-1 space-y-1 border-l pl-3"
        >
          {quotes.map((quote, i) => (
            // The index is part of the key because two identical quotes from
            // two places are the normal case here, not a mistake: that is what
            // "independent occurrences" means.
            <li key={`${i}-${quote}`} className="break-words text-xs italic text-muted-foreground">
              {quote}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export default function SuggestedMemories({ candidates, onAccept, onDismiss,
                                           onAcceptAll, acceptingAll = false,
                                           busyId }) {
  const { t, taal } = useTaal();
  const [openId, setOpenId] = useState(null);
  // TREDE 2, MASTERPLAN 3ax-4. Welke kaart wordt herschreven, en waarin.
  //
  // **EEN KAART TEGELIJK, EN DICHT TOT JE EROM VRAAGT.** Een tekstvak dat
  // altijd openstaat nodigt uit tot herschrijven, en dan keurt de teller trede
  // 3 af op een eigenschap van het SCHERM in plaats van van het model.
  const [bewerktId, setBewerktId] = useState(null);
  const [bewerkt, setBewerkt] = useState("");

  // Nothing waiting means nothing on screen. See the header: an empty box
  // teaches you to skip the place where the real ones will appear.
  if (!candidates || candidates.length === 0) return null;

  return (
    <section
      className="mb-4 max-w-prose rounded-lg border border-dashed p-3"
      data-testid="suggested-block"
      aria-label={t("Voorgestelde herinneringen")}
    >
      <h2 className="text-sm font-medium">
        {t("Voorgesteld")}
        <span className="ml-2 text-xs font-normal text-muted-foreground" data-testid="suggested-count">
          {t("{aantal} wachtend", { aantal: candidates.length })}
        </span>
      </h2>
      <p className="mt-1 text-xs text-muted-foreground">
        {t("Opgemerkt, niet bewaard. Niets hiervan wordt in een antwoord gebruikt tot je het aanneemt.")}
      </p>

      {/* HET AANTAL STAAT IN HET OPSCHRIFT, en dat is de eis en niet de smaak.
          "Accept all" zonder getal vraagt om ja tegen iets waarvan je niet ziet
          hoe groot het is; met het getal erin is de knop zijn eigen
          bevestiging. Franks verzoek van 24 augustus 2026 was letterlijk
          "Accept all 10".

          Er komt GEEN tussenscherm dat nog eens vraagt of je het zeker weet.
          Alles hier is met een klik per regel terug te draaien, en een dialoog
          die niets toevoegt leert mensen om dialogen weg te klikken. */}
      {onAcceptAll && candidates.length > 1 && (
        <div className="mt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onAcceptAll}
            disabled={acceptingAll || Boolean(busyId)}
            data-testid="suggested-accept-all"
          >
            {acceptingAll
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <Check className="mr-2 h-4 w-4" />}
            {t("Neem alle {aantal} aan", { aantal: candidates.length })}
          </Button>
        </div>
      )}

      <ul className="mt-3 space-y-3" data-testid="suggested-list">
        {candidates.map((c) => (
          <li key={c.id} data-testid={`suggested-${c.id}`} className="border-t pt-3 first:border-t-0 first:pt-0">
            {/* min-w-0 or a long sentence pushes the buttons off the row, the
                same trap the memory list below already fell into once. */}
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                {bewerktId === c.id ? (
                  <textarea
                    value={bewerkt}
                    onChange={(e) => setBewerkt(e.target.value)}
                    data-testid={`suggested-text-${c.id}`}
                    aria-label={t("Herschrijf voor het onthouden: {tekst}", { tekst: c.text })}
                    rows={2}
                    className="w-full resize-y rounded border bg-background p-1.5 text-sm"
                  />
                ) : (
                  <p className="break-words text-sm">{c.text}</p>
                )}
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {c.reason}
                  {c.reason && c.source ? " · " : ""}
                  {herkomstTekst(c, t, taal)}
                  {c.seen_count > 1 ? ` · ${t("{aantal} keer gezien", { aantal: c.seen_count })}` : ""}
                </p>
                <Citaten
                  id={c.id}
                  quotes={c.evidence}
                  open={openId === c.id}
                  onToggle={() => setOpenId(openId === c.id ? null : c.id)}
                />
              </div>
              <div className="flex shrink-0 gap-1">
                {/* HERSCHRIJVEN IS EEN EIGEN KNOP EN GEEN KLIK OP DE TEKST.
                    Een regel die in een invoerveld verandert zodra je hem
                    aanraakt, verandert ook wanneer je hem alleen wilde lezen. */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="tap-target h-8 w-8"
                  disabled={busyId === c.id}
                  onClick={() => {
                    setBewerktId(bewerktId === c.id ? null : c.id);
                    setBewerkt(c.text);
                  }}
                  aria-label={t("Herschrijf voor het onthouden: {tekst}", { tekst: c.text })}
                  aria-expanded={bewerktId === c.id}
                  data-testid={`suggested-edit-${c.id}`}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="tap-target h-8 w-8"
                  disabled={busyId === c.id}
                  // `undefined` en niet `c.text` wanneer er niets is
                  // herschreven: de dienst leest "geen tekst" als "neem het
                  // over zoals het er staat". Altijd de tekst meesturen zou
                  // werken, maar dan hangt de telling af van wat het scherm
                  // invult in plaats van van wat jij deed.
                  onClick={() => onAccept(c, bewerktId === c.id ? bewerkt : undefined)}
                  aria-label={t("Onthoud: {tekst}", { tekst: c.text })}
                  data-testid={`suggested-accept-${c.id}`}
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="tap-target h-8 w-8"
                  disabled={busyId === c.id}
                  onClick={() => onDismiss(c)}
                  // Said in full, because this one does not come back and the
                  // label is the last chance to know that before pressing it.
                  aria-label={t("Nee, stel dit nooit meer voor: {tekst}", { tekst: c.text })}
                  data-testid={`suggested-dismiss-${c.id}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
